/**
 * 评估服务
 * 处理图片评估相关的 API 调用
 *
 * @see Requirements 6.1, 6.2, 6.3, 6.4, 6.5
 */

// ============ 类型定义 ============

/** 评估状态 */
export type AssessmentStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "timeout_error"
  | "unlocked_care_plan"
  | "pending_analysis"
  | "pending_prescreen";

/** 评估记录 */
export interface Assessment {
  id: number;
  friendly_id: string;
  analysis_result: string | null;
  completed_at: string | null;
  status: AssessmentStatus;
  error_message: string | null;
  image_url: string | null;
  thumb_url: string | null;
  user_query: string | null;
  created_at: string;
  unlocked?: boolean;
  tank_id?: number | null;
  tank_name?: string | null;
  // 数据飞轮新增字段
  image_hash?: string; // 图片哈希（去重用）
  detected_disease_code?: string; // AI 检测的病害代码
  ai_confidence?: number; // AI 置信度 0-100
  cached?: boolean; // 是否为缓存命中
  cached_from_id?: number; // 缓存来源 ID
  cached_at?: string; // 缓存时间
  // 分离的诊断和治疗数据
  stage_2_public?: {
    fish_species: string;
    detected_disease_code: string;
    assessment_title: string;
    severity_level: "low" | "medium" | "high";
    confidence_score: number;
    visual_evidence: Array<{
      body_part: string;
      symptom: string;
      bbox_x?: number;
      bbox_y?: number;
    }>;
    analysis_summary: string;
    urgency_teaser: string;
  };
  stage_3_private?: {
    root_causes: string[];
    care_steps: Array<{
      phase: string;
      action: string;
      toolbox_item_used: string | null;
      toolbox_match?: {
        matched: boolean;
        item_name?: string;
        item_type?: string;
      };
    }>;
    prognosis_note: string;
    sop_source?: {
      disease_code: string;
      sop_version?: string;
      is_fallback: boolean;
    };
    readiness_score?: number;
  };
  // 反馈相关字段（旧版，保留向后兼容）
  user_feedback?: "positive" | "negative" | null;
  user_feedback_reason?: string | null;
  // 反馈状态（新版，来自 AssessmentConversionSerializer）
  feedback_status?: {
    user_feedback: "positive" | "negative" | null;
    feedback_submitted_at: string | null;
    can_submit_feedback: boolean;
  };
}

/** 分页元数据 */
export interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

/** 评估列表响应 */
export interface AssessmentsResponse {
  assessments: Assessment[];
  meta: PaginationMeta;
}

/** 创建评估请求参数 */
export interface CreateAssessmentRequest {
  image?: File;
  image_url?: string;
  user_query?: string;
  toolbox_items?: string; // JSON string
  tank_id?: number;
  idempotencyKey?: string;
}

/** 创建评估响应 */
export interface CreateAssessmentResponse {
  id: number;
  friendly_id: string;
  status: AssessmentStatus;
  job_status_id?: number;
  message?: string;
  points?: {
    frozen: number;
    frozen_at: string;
  };
}

/** 分析图片请求参数 */
export interface AnalyzeImageRequest {
  image?: File;
  image_url?: string;
  user_query?: string;
  toolbox_items?: string; // JSON string
  tank_id?: number;
}

/** 分析结果 */
export interface AnalysisResult {
  assessment: string;
  confidence: number;
  suggestions: string[];
}

/** 分析图片响应 */
export interface AnalyzeImageResponse {
  success: boolean;
  result?: AnalysisResult;
  error?: string;
}

/** 预筛选请求参数 */
export interface PrescreenRequest {
  image: File;
}

/** 预筛选成功响应 */
export interface PrescreenSuccessResponse {
  passed: true;
  fish_class: string | null;
  confidence: number | null;
  quality_score: number | null;
  process_time_ms: number | null;
}

/** 预筛选失败响应 */
export interface PrescreenFailureResponse {
  passed: false;
  reason: string;
  message: string;
}

/** 预筛选响应 */
export type PrescreenResponse =
  | PrescreenSuccessResponse
  | PrescreenFailureResponse;

/** 提交反馈响应 */
export interface SubmitFeedbackResponse {
  success: boolean;
  message: string;
  reward?: {
    points: number;
    message: string;
  };
}

import { ApiError } from "@/types/apiErrors";

/** API 错误响应 */
export interface ApiErrorResponse {
  error?: string;
  errors?: Record<string, string[]>;
}

// ============ 请求参数类型 ============

/** 获取评估列表参数 */
export interface GetAssessmentsParams {
  page?: number;
  per_page?: number;
}

// ============ 请求配置 ============

/** 请求配置 */
interface RequestConfig {
  timeout?: number; // 超时时间（毫秒），默认 30000
  retries?: number; // 重试次数，默认 3
  retryDelay?: number; // 初始重试延迟（毫秒），默认 1000
  headers?: Record<string, string>; // 自定义请求头
}

const DEFAULT_CONFIG: Required<RequestConfig> = {
  timeout: 30000,
  retries: 3,
  retryDelay: 1000,
  headers: {},
};

// ============ 辅助函数 ============

const API_BASE_URL = "/api/v1";

/**
 * 获取 CSRF Token
 */
export function getCSRFToken(): string {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  );
  return meta?.content || "";
}

/**
 * 构建带查询参数的 URL
 */
function buildURL(endpoint: string, params?: Record<string, any>): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * 延迟函数
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 带超时的 fetch
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * 判断是否应该重试
 */
function shouldRetry(error: unknown, response?: Response): boolean {
  // 超时错误应该重试
  if (error instanceof Error && error.name === "AbortError") {
    return true;
  }

  // 网络错误应该重试
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return true;
  }

  // 5xx 服务器错误应该重试
  if (response && response.status >= 500) {
    return true;
  }

  // 429 Too Many Requests 应该重试
  if (response && response.status === 429) {
    return true;
  }

  // 402 Payment Required 不应该重试 (积分不足)
  if (response && response.status === 402) {
    return false;
  }

  return false;
}

/**
 * 带重试的请求
 */
async function requestWithRetry<T>(
  url: string,
  options: RequestInit,
  config: Required<RequestConfig>,
): Promise<T> {
  let lastError: Error | null = null;
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= config.retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, config.timeout);
      lastResponse = response;

      // 204 No Content
      if (response.status === 204) {
        return undefined as T;
      }

      const json = await response.json();

      if (!response.ok) {
        // 处理新版 API 响应格式 { success: false, error: { code, message, details } }
        // 以及旧版格式 { error: "message" } 或 { errors: { base: ["message"] } }
        let errorMessage: string;
        if (typeof json.error === "object" && json.error?.message) {
          errorMessage = json.error.message;
        } else if (typeof json.error === "string") {
          errorMessage = json.error;
        } else if (json.errors?.base?.[0]) {
          errorMessage = json.errors.base[0];
        } else {
          errorMessage = `请求失败：${response.status}`;
        }

        // 如果不应该重试，直接抛出错误
        if (!shouldRetry(null, response)) {
          throw new ApiError(errorMessage, response.status, json);
        }

        lastError = new ApiError(errorMessage, response.status, json);
      } else {
        // 处理新版 API 响应格式 { success: true, data: {...} }
        // 如果响应包含 data 字段，返回 data，否则返回整个 json（兼容旧版 API）
        if (json.success === true && json.data !== undefined) {
          return json.data as T;
        }
        return json as T;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // 如果不应该重试，直接抛出
      if (!shouldRetry(error, lastResponse ?? undefined)) {
        throw lastError;
      }
    }

    // 如果还有重试机会，等待后重试
    if (attempt < config.retries) {
      // 指数退避：1s, 2s, 4s...
      const waitTime = config.retryDelay * Math.pow(2, attempt);
      await delay(waitTime);
    }
  }

  // 所有重试都失败了
  throw lastError || new Error("请求失败");
}

/**
 * 发起 GET 请求
 */
export async function get<T>(
  endpoint: string,
  params?: Record<string, any>,
  config: RequestConfig = {},
): Promise<T> {
  const url = buildURL(endpoint, params);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  return requestWithRetry<T>(
    url,
    {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
    },
    mergedConfig,
  );
}

/**
 * 发起 DELETE 请求
 */
async function del<T>(
  endpoint: string,
  config: RequestConfig = {},
): Promise<T> {
  const url = buildURL(endpoint);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  return requestWithRetry<T>(
    url,
    {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
    },
    mergedConfig,
  );
}

/**
 * 发起 POST 请求（FormData）
 */
async function postFormData<T>(
  endpoint: string,
  formData: FormData,
  config: RequestConfig = {},
): Promise<T> {
  const url = buildURL(endpoint);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-CSRF-Token": getCSRFToken(),
  };

  // 如果配置中有 header，合并进去
  if (config.headers) {
    Object.assign(headers, config.headers);
  }

  return requestWithRetry<T>(
    url,
    {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: formData,
    },
    mergedConfig,
  );
}

/**
 * 发起 POST 请求（FormData）- 用于预筛选，不抛出错误
 */
async function postFormDataNoThrow<T>(
  endpoint: string,
  formData: FormData,
  config: RequestConfig = {},
): Promise<T> {
  const url = buildURL(endpoint);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: formData,
      },
      mergedConfig.timeout,
    );

    const json = await response.json();

    // 处理 401 未授权错误 - 返回特殊的失败响应
    if (response.status === 401) {
      const errorMessage =
        typeof json.error === "object" && json.error?.message
          ? json.error.message
          : typeof json.error === "string"
            ? json.error
            : "请先登录";
      return {
        passed: false,
        reason: "unauthorized",
        message: errorMessage,
      } as T;
    }

    // 其他情况返回 JSON 响应
    return json as T;
  } catch (error) {
    // 超时或网络错误时返回失败响应
    return {
      passed: false,
      reason: "network_error",
      message: error instanceof Error ? error.message : "网络请求失败",
    } as T;
  }
}

// ============ 服务类 ============

/**
 * 评估服务类
 * 提供图片评估相关的 API 操作
 */
class AssessmentService {
  /**
   * 获取评估记录列表
   * @see Requirements 6.2
   *
   * @param params 分页参数
   * @returns 评估列表和分页信息
   */
  async getAssessments(
    params: GetAssessmentsParams = {},
  ): Promise<AssessmentsResponse> {
    const { page = 1, per_page = 10 } = params;

    return get<AssessmentsResponse>("/assessments", { page, per_page });
  }

  /**
   * 获取单个评估记录
   *
   * @param id 评估记录 ID 或 friendly_id
   * @returns 评估记录详情
   */
  async getAssessment(id: string | number): Promise<Assessment> {
    // get() 函数已自动从 { success, data, meta } 格式中提取 data
    return get<Assessment>(`/assessments/${id}`);
  }

  /**
   * 创建新评估
   * @see Requirements 6.3
   *
   * @param data 创建评估的数据
   * @returns 创建结果，包含任务状态 ID
   */
  async createAssessment(
    data: CreateAssessmentRequest,
  ): Promise<CreateAssessmentResponse> {
    const formData = new FormData();

    if (data.image) {
      formData.append("image", data.image);
    }
    if (data.image_url) {
      formData.append("image_url", data.image_url);
    }
    if (data.user_query) {
      formData.append("user_query", data.user_query);
    }
    if (data.toolbox_items) {
      formData.append("toolbox_items", data.toolbox_items);
    }
    if (data.tank_id) {
      formData.append("tank_id", String(data.tank_id));
    }

    const config: RequestConfig = {};
    if (data.idempotencyKey) {
      config.headers = {
        "X-Idempotency-Key": data.idempotencyKey,
      };
    }

    return postFormData<CreateAssessmentResponse>(
      "/assessments",
      formData,
      config,
    );
  }

  /**
   * 实时分析图片（不保存记录）
   * @see Requirements 6.4
   *
   * @param data 分析请求数据
   * @returns 分析结果
   */
  async analyzeImage(data: AnalyzeImageRequest): Promise<AnalyzeImageResponse> {
    const formData = new FormData();

    if (data.image) {
      formData.append("image", data.image);
    }
    if (data.image_url) {
      formData.append("image_url", data.image_url);
    }
    if (data.user_query) {
      formData.append("user_query", data.user_query);
    }
    if (data.toolbox_items) {
      formData.append("toolbox_items", data.toolbox_items);
    }
    if (data.tank_id) {
      formData.append("tank_id", String(data.tank_id));
    }

    return postFormData<AnalyzeImageResponse>("/assessments/analyze", formData);
  }

  /**
   * 删除评估记录
   * @see Requirements 6.5
   *
   * @param id 评估记录 ID 或 friendly_id
   */
  async deleteAssessment(id: string | number): Promise<void> {
    await del<void>(`/assessments/${id}`);
  }

  /**
   * 图片预筛选
   * 在用户上传图片后立即调用，快速检查图片是否为有效鱼类图片
   *
   * @param data 预筛选请求数据
   * @returns 预筛选结果（passed: true/false）
   */
  async prescreenImage(data: PrescreenRequest): Promise<PrescreenResponse> {
    const formData = new FormData();
    formData.append("image", data.image);

    // 使用不抛出错误的版本，因为预筛选失败是正常业务逻辑
    // 预筛选超时设置为 15 秒，不重试
    return postFormDataNoThrow<PrescreenResponse>(
      "/assessments/prescreen",
      formData,
      { timeout: 15000, retries: 0 },
    );
  }

  /**
   * 提交用户反馈
   *
   * @param id 评估 ID 或 friendly_id
   * @param feedback 反馈类型 'positive' | 'negative'
   * @param details 反馈详情（可选）
   * @returns 反馈提交结果，包含积分奖励信息
   */
  async submitFeedback(
    id: string | number,
    feedback: "positive" | "negative",
    details?: string,
  ): Promise<SubmitFeedbackResponse> {
    const formData = new FormData();
    formData.append("feedback", feedback);
    if (details) {
      formData.append("details", details);
    }
    return postFormData<SubmitFeedbackResponse>(
      `/assessments/${id}/feedback`,
      formData,
    );
  }

  /**
   * 解锁护理方案 (积分消费)
   *
   * @param id 评估 ID 或 friendly_id
   */
  async unlockCarePlan(id: string | number): Promise<{
    success: boolean;
    data: {
      message: string;
      unlock_info: { cost: number; promotion: string };
    };
  }> {
    const formData = new FormData();
    // 空 FormData，仅用于触发 POST 请求
    return postFormData(`/assessments/${id}/unlock_care`, formData);
  }
}

// 导出单例
export const assessmentService = new AssessmentService();
export default assessmentService;
